<template>
    <div class="animated fadeIn">
        <b-container fluid class="p-0">
            <b-row>
                <b-col sm="12" md="8" class="my-2">
                    <b-card no-body>
                        <b-card-header header-class="dark-header">
                            <span class="font-weight-bold text-white">Loyalty</span>
                        </b-card-header>
                        <b-card-body>
                            <div class="d-flex flex-row justify-content-between align-items-center mb-3">
                                <p class="mb-0">Loyalty Program</p>
                                <c-switch
                                        color="warning"
                                        v-model="model.LoyaltyAllowance"
                                        @click="toggleProgramField('LoyaltyAllowance', model.LoyaltyAllowance)"
                                        label variant="3d" dataOn="on" dataOff="off"/>
                            </div>
                            <div class="d-flex flex-row justify-content-between align-items-center mb-3" v-if="apiKey">
                                <p class="mb-0 text-muted">API Key</p>
                                <div class="d-flex flex-row align-items-center">
                                    <code class="mr-2">{{ apiKey }}</code>
                                    <b-btn variant="link" class="text-decoration-none p-0" v-clipboard:copy="apiKey" v-clipboard:success="onCopyApiKey">Copy</b-btn>
                                </div>
                            </div>
                            <span class="small d-block text-right" style="color: #4700ff" v-if="copiedMessage">{{ copiedMessage }}</span>
                            <hr/>
                            <div>
                                <b-input-group :class="!editAmountFactorMode ? 'deactivePrepend' : ''" prepend="Point Earning Amount Factor (points per $1 spent)">
                                    <template v-slot:append>
                                        <b-btn class="text-decoration-none" :variant="editAmountFactorMode ? 'success' : 'link'" @click="editAmountFactor">
                                            <span v-if="!editAmountFactorMode">Edit</span>
                                            <span v-else>Apply</span>
                                        </b-btn>
                                    </template>
                                    <b-form-input class="text-center" :class="!editAmountFactorMode ? 'removeBorder' : ''" :disabled="!editAmountFactorMode" type="number" min="0" step="1" @keypress="preventDecimal" v-model="model.AmountFactor"/>
                                </b-input-group>
                            </div>
                            <hr/>
                            <div class="mb-4">
                                <h5 class="font-weight-bold">Rewards</h5>
                                <b-table-simple v-if="rewards.length" striped borderless responsive>
                                    <b-thead>
                                        <b-tr class="bg-gray-300">
                                            <b-th>Reward Name</b-th>
                                            <b-th>Status</b-th>
                                            <b-th>Type</b-th>
                                            <b-th>Point Threshold</b-th>
                                            <b-th>Applies to</b-th>
                                            <b-th></b-th>
                                        </b-tr>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="(item, index) in rewards" :key="item.RewardID || index" class="cursor-pointer" @click="showEditReward(item)">
                                            <b-td>{{ item.Name }}</b-td>
                                            <b-td>
                                                <b-badge pill :variant="item.Status === 'active' ? 'warning' : 'secondary'">
                                                    {{ item.Status === 'active' ? 'Active' : 'Inactive' }}
                                                </b-badge>
                                            </b-td>
                                            <b-td>{{ rewardTypeLabel(item.RewardType) }}</b-td>
                                            <b-td>{{ item.PointsThreshold }}</b-td>
                                            <b-td>{{ appliesToLabel(item) }}</b-td>
                                            <b-td>
                                                <font-awesome-icon icon="pencil-alt" class="cursor-pointer"/>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                                <p v-else class="text-muted">No rewards have been created yet.</p>
                                <b-btn @click="showAddReward" variant="danger">
                                    Add Rewards
                                    <font-awesome-icon icon="plus" class="align-middle mx-1"/>
                                </b-btn>
                            </div>
                            <hr/>
                            <div class="mb-4">
                                <h5 class="font-weight-bold">Point Earning by Order Type</h5>
                                <b-table-simple v-if="orderTypeFactors.length" striped borderless responsive>
                                    <b-thead>
                                        <b-tr class="bg-gray-300">
                                            <b-th>Order type</b-th>
                                            <b-th>Multiplier</b-th>
                                            <b-th>Fixed bonus points</b-th>
                                            <b-th></b-th>
                                        </b-tr>
                                    </b-thead>
                                    <b-tbody>
                                        <b-tr v-for="(item, index) in orderTypeFactors" :key="item.OrderTypeCode || index">
                                            <b-td>{{ orderTypeLabel(item.OrderTypeCode) }}</b-td>
                                            <b-td>{{ Math.round(item.Multiplier) }}</b-td>
                                            <b-td>{{ Math.round(item.FixedBonusPoints) }}</b-td>
                                            <b-td>
                                                <b-btn variant="link" class="text-decoration-none p-0" @click="showEditOrderType(item)">
                                                    Edit
                                                </b-btn>
                                            </b-td>
                                        </b-tr>
                                    </b-tbody>
                                </b-table-simple>
                                <p v-else class="text-muted">No order type earning rules yet.</p>
                            </div>
                            <hr/>
                            <div>
                                <h5 class="font-weight-bold">Redemption &amp; Combination</h5>
                                <div class="d-flex flex-row justify-content-between align-items-center mb-3">
                                    <p class="mb-0">Allow multiple reward redemptions per order</p>
                                    <c-switch
                                            color="warning"
                                            v-model="model.AllowMultipleRedemptionsPerOrder"
                                            @click="toggleProgramField('AllowMultipleRedemptionsPerOrder', model.AllowMultipleRedemptionsPerOrder)"
                                            label variant="3d" dataOn="on" dataOff="off"/>
                                </div>
                                <div class="d-flex flex-row justify-content-between align-items-center mb-3">
                                    <p class="mb-0">Allow combining a promotion with loyalty redemption</p>
                                    <c-switch
                                            color="warning"
                                            v-model="model.AllowPromoCombination"
                                            @click="toggleProgramField('AllowPromoCombination', model.AllowPromoCombination)"
                                            label variant="3d" dataOn="on" dataOff="off"/>
                                </div>
                                <div class="d-flex flex-row justify-content-between align-items-center" :class="model.InactivityExpirationEnabled ? 'mb-3' : ''">
                                    <p class="mb-0">Expire All Points After Inactivity Period</p>
                                    <c-switch
                                            color="warning"
                                            v-model="model.InactivityExpirationEnabled"
                                            @click="toggleProgramField('InactivityExpirationEnabled', model.InactivityExpirationEnabled)"
                                            label variant="3d" dataOn="on" dataOff="off"/>
                                </div>
                                <div class="d-flex flex-row justify-content-between align-items-center pl-3" v-if="model.InactivityExpirationEnabled">
                                    <p class="mb-0 text-muted">Inactivity Period (Days)</p>
                                    <div class="d-flex flex-row align-items-center">
                                        <b-form-input
                                                v-if="editInactivityMode"
                                                type="number"
                                                min="1"
                                                step="1"
                                                @keypress="preventDecimal"
                                                size="sm"
                                                class="text-center mr-2"
                                                style="width: 100px"
                                                v-model="model.InactivityExpirationDays"/>
                                        <span v-else class="mr-3">{{ model.InactivityExpirationDays != null ? Math.round(model.InactivityExpirationDays) : '' }}</span>
                                        <b-btn variant="link" class="text-decoration-none p-0" @click="editInactivityPeriod">
                                            <span v-if="!editInactivityMode">Edit</span>
                                            <span v-else>Apply</span>
                                        </b-btn>
                                    </div>
                                </div>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>

            <c-modal size="md" centered v-model="rewardModalVisible" :tag="false" :title="rewardModalMode === 'add' ? 'Add Loyalty Reward' : 'Edit Loyalty Reward'">
                <template>
                    <b-alert :show="errors.any()" variant="danger" class="px-4">
                        <ul class="m-0 px-1">
                            <li v-for="err in errors.all()" :key="err">{{ err }}</li>
                        </ul>
                    </b-alert>
                    <b-row>
                        <b-col :cols="rewardModalMode === 'edit' ? 8 : 12">
                            <label class="mb-1">Reward name</label>
                            <b-form-input placeholder="Required" name="Reward name" v-validate="'required'" v-model="rewardModel.Name"/>
                        </b-col>
                        <b-col cols="4" v-if="rewardModalMode === 'edit'">
                            <label class="mb-1 d-block">Status</label>
                            <c-switch color="warning" v-model="rewardModel.Status" label variant="3d" dataOn="on" dataOff="off"/>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <label class="mb-1">Points threshold to unlock</label>
                            <b-form-input type="number" min="0" placeholder="Required" name="Points threshold to unlock" v-validate="'required'" v-model="rewardModel.PointsThreshold"/>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <label class="mb-1">Reward type</label>
                            <b-form-select :options="rewardTypeOptions" v-model="rewardModel.RewardType"/>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3" v-if="rewardModel.RewardType !== 'free_item'">
                        <b-col cols="12">
                            <label class="mb-1">Discount value</label>
                            <b-input-group :prepend="rewardModel.RewardType === 'fixed_amount_off' ? '$' : null" :append="rewardModel.RewardType === 'fixed_percent_off' ? '%' : null">
                                <b-form-input type="number" min="0" placeholder="Required" name="Discount value" v-validate="'required'" v-model="rewardModel.Value"/>
                            </b-input-group>
                            <small class="text-muted">off the order subtotal</small>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="12">
                            <label class="mb-1">Applies to</label>
                            <b-form-select :options="appliesToOptions" v-model="rewardModel.AppliesTo"/>
                            <small class="text-muted d-block mt-1">* Tag items in My Menu page</small>
                        </b-col>
                    </b-row>
                </template>
                <template #actions>
                    <b-row class="flex-grow-1">
                        <b-col cols="6">
                            <b-btn variant="warning" block class="text-nowrap" :disabled="loading" @click="saveReward">
                                Save Reward
                                <font-awesome-icon v-if="loading" spin icon="circle-notch" class="text-black align-middle mx-1"/>
                            </b-btn>
                        </b-col>
                        <b-col cols="6">
                            <b-btn variant="light" block class="text-nowrap" @click="rewardModalVisible = false">
                                Cancel
                            </b-btn>
                        </b-col>
                    </b-row>
                </template>
            </c-modal>

            <c-modal size="md" centered v-model="orderTypeModalVisible" :tag="false" title="Edit Point Earning">
                <template>
                    <b-alert :show="errors.any()" variant="danger" class="px-4">
                        <ul class="m-0 px-1">
                            <li v-for="err in errors.all()" :key="err">{{ err }}</li>
                        </ul>
                    </b-alert>
                    <div class="mb-3">
                        <label class="mb-1">Order type</label>
                        <b-form-input :value="orderTypeLabel(orderTypeModel.OrderTypeCode)" disabled/>
                    </div>
                    <div class="mb-3">
                        <label class="mb-1">Multiplier</label>
                        <b-form-input type="number" min="0" step="1" @keypress="preventDecimal" placeholder="Required" name="Multiplier" v-validate="'required'" v-model="orderTypeModel.Multiplier"/>
                    </div>
                    <div class="mb-3">
                        <label class="mb-1">Fixed bonus points</label>
                        <b-form-input type="number" min="0" step="1" @keypress="preventDecimal" placeholder="Required" name="Fixed bonus points" v-validate="'required'" v-model="orderTypeModel.FixedBonusPoints"/>
                    </div>
                </template>
                <template #actions>
                    <b-row class="flex-grow-1">
                        <b-col cols="6">
                            <b-btn variant="warning" block class="text-nowrap" :disabled="loading" @click="saveOrderTypeFactors">
                                Save changes
                                <font-awesome-icon v-if="loading" spin icon="circle-notch" class="text-black align-middle mx-1"/>
                            </b-btn>
                        </b-col>
                        <b-col cols="6">
                            <b-btn variant="light" block class="text-nowrap" @click="orderTypeModalVisible = false">
                                Cancel
                            </b-btn>
                        </b-col>
                    </b-row>
                </template>
            </c-modal>

            <c-modal size="md" centered v-model="apiCredentialsModalVisible" :tag="false" title="Loyalty API Credentials" no-submit confirm-label="Done">
                <template>
                    <b-alert show variant="warning">
                        Copy these now. The API secret is only shown once and cannot be retrieved again.
                    </b-alert>
                    <div class="mb-3">
                        <label class="mb-1">API Key</label>
                        <b-input-group>
                            <b-form-input readonly :value="newApiCredentials.apiKey"/>
                            <b-input-group-append>
                                <b-button variant="success" v-clipboard:copy="newApiCredentials.apiKey" v-clipboard:success="onCopyApiKey">Copy</b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="mb-3">
                        <label class="mb-1">API Secret</label>
                        <b-input-group>
                            <b-form-input readonly :value="newApiCredentials.apiSecret"/>
                            <b-input-group-append>
                                <b-button variant="success" v-clipboard:copy="newApiCredentials.apiSecret" v-clipboard:success="onCopyApiSecret">Copy</b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                </template>
            </c-modal>
        </b-container>
    </div>
</template>

<script>
    import { Switch as cSwitch } from '@/components/shared'
    import CModal from "../shared/Cmodal";
    import validation from "../../mixins/validation";

    const ORDER_TYPE_LABELS = {
        KIOSK: 'Kiosk order',
        ONLINE_PICKUP: 'Online pickup',
        ONLINE_DELIVERY: 'Online delivery',
        POS_TAKEOUT: 'POS takeout/delivery',
        POS_DELIVERY: 'POS takeout/delivery',
        POS_TAKEOUT_DELIVERY: 'POS takeout/delivery',
        POS_DINEIN: 'POS dine-in',
        DINEIN: 'POS dine-in',
    }

    export default {
        name: "loyalty",
        components: {
            CModal,
            cSwitch
        },
        mixins: [validation],
        data () {
            return {
                loading: false,
                editAmountFactorMode: false,
                editInactivityMode: false,
                copiedMessage: null,
                apiKey: null,
                apiCredentialsModalVisible: false,
                newApiCredentials: {
                    apiKey: null,
                    apiSecret: null,
                },
                model: {
                    LoyaltyAllowance: false,
                    AmountFactor: 1,
                    AllowMultipleRedemptionsPerOrder: false,
                    AllowPromoCombination: false,
                    InactivityExpirationEnabled: false,
                    InactivityExpirationDays: null,
                },
                rewards: [],
                rewardModalVisible: false,
                rewardModalMode: 'add',
                rewardModel: {
                    RewardID: 0,
                    Name: null,
                    Status: true,
                    _originalStatus: true,
                    PointsThreshold: null,
                    RewardType: 'fixed_amount_off',
                    Value: null,
                    AppliesTo: 'AllMenus',
                },
                rewardTypeOptions: [
                    {value: 'fixed_amount_off', text: 'Fixed Amount off'},
                    {value: 'fixed_percent_off', text: 'Percent off'},
                    {value: 'free_item', text: 'Free Item'},
                ],
                appliesToOptions: [
                    {value: 'AllMenus', text: 'All Menus (Default)'},
                    {value: 'SelectedItems', text: 'Selected Items*'},
                ],
                orderTypeFactors: [],
                orderTypeModalVisible: false,
                orderTypeModel: {
                    OrderTypeCode: null,
                    Multiplier: null,
                    FixedBonusPoints: null,
                },
            }
        },
        metaInfo () {
            return {
                title: 'Loyalty settings'
            }
        },
        watch: {
            '$store.state.selectedRestaurantId': {
                handler: function (newVal) {
                    if (newVal && newVal !== null) {
                        this.GetSettings()
                    }
                },
                immediate: true
            }
        },
        methods: {
            preventDecimal (evt) {
                if (evt.key === '.' || evt.key === ',' || evt.key === 'e' || evt.key === 'E' || evt.key === '-') {
                    evt.preventDefault()
                }
            },
            rewardTypeLabel (type) {
                const found = this.rewardTypeOptions.find(item => item.value === type)
                return found ? found.text : type
            },
            appliesToLabel (reward) {
                if (Array.isArray(reward.Scope) && reward.Scope.length > 0) {
                    return `Selected Items (${reward.Scope.length})`
                }
                return 'All Menus'
            },
            orderTypeLabel (code) {
                if (ORDER_TYPE_LABELS[code]) return ORDER_TYPE_LABELS[code]
                if (!code) return ''
                return code.toString().toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            },
            GetSettings () {
                const object = {RestaurantID: Number(this.$store.state.selectedRestaurantId)}
                this.$api.loyaltyAdmin.GetSettings(object).then(res => {
                    if (res.success) {
                        const results = res.results || {}
                        this.model.LoyaltyAllowance = !!results.LoyaltyAllowance
                        this.model.AmountFactor = results.AmountFactor != null ? results.AmountFactor : 1
                        this.model.AllowMultipleRedemptionsPerOrder = !!results.AllowMultipleRedemptionsPerOrder
                        this.model.AllowPromoCombination = !!results.AllowPromoCombination
                        this.model.InactivityExpirationEnabled = !!results.InactivityExpirationEnabled
                        this.model.InactivityExpirationDays = results.InactivityExpirationDays
                        this.apiKey = results.ApiKey || null
                        this.orderTypeFactors = (results.OrderTypeFactors || results.Factors || []).map(item => ({...item}))
                        this.rewards = (results.Rewards || []).map(item => ({...item}))
                    } else {
                        this.$showError(res.message)
                    }
                })
            },
            buildProgramPayload () {
                return {
                    RestaurantID: Number(this.$store.state.selectedRestaurantId),
                    AmountFactor: this.model.AmountFactor !== null && this.model.AmountFactor !== '' ? Math.round(Number(this.model.AmountFactor)) : null,
                    InactivityExpirationEnabled: this.model.InactivityExpirationEnabled,
                    InactivityExpirationDays: this.model.InactivityExpirationDays !== null && this.model.InactivityExpirationDays !== '' ? Math.round(Number(this.model.InactivityExpirationDays)) : null,
                    AllowMultipleRedemptionsPerOrder: this.model.AllowMultipleRedemptionsPerOrder,
                    AllowPromoCombination: this.model.AllowPromoCombination,
                    LoyaltyAllowance: this.model.LoyaltyAllowance,
                }
            },
            saveProgramSettings (overrides) {
                const object = {...this.buildProgramPayload(), ...overrides}
                return this.$api.loyaltyAdmin.SaveProgram(object).then(res => {
                    if (res.success) {
                        if (res.apiKey) {
                            this.apiKey = res.apiKey
                        }
                        if (res.apiSecret) {
                            this.newApiCredentials = {apiKey: res.apiKey, apiSecret: res.apiSecret}
                            this.apiCredentialsModalVisible = true
                        }
                    } else {
                        this.$showError(res.message)
                    }
                    return res
                })
            },
            toggleProgramField (field, currentValue) {
                this.saveProgramSettings({[field]: !currentValue})
            },
            editAmountFactor () {
                if (!this.editAmountFactorMode) {
                    this.editAmountFactorMode = true
                } else if (this.model.AmountFactor === null || this.model.AmountFactor === '') {
                    this.$showError('Point Earning Amount Factor must not be empty')
                } else {
                    this.saveProgramSettings({}).then(res => {
                        if (res.success) {
                            this.editAmountFactorMode = false
                        }
                    })
                }
            },
            editInactivityPeriod () {
                if (!this.editInactivityMode) {
                    this.editInactivityMode = true
                } else if (this.model.InactivityExpirationDays === null || this.model.InactivityExpirationDays === '') {
                    this.$showError('Inactivity Period must not be empty')
                } else {
                    this.saveProgramSettings({}).then(res => {
                        if (res.success) {
                            this.editInactivityMode = false
                        }
                    })
                }
            },
            onCopyApiKey () {
                this.copiedMessage = 'API Key copied'
            },
            onCopyApiSecret () {
                this.copiedMessage = 'API Secret copied'
            },
            showAddReward () {
                this.rewardModalMode = 'add'
                this.rewardModel = {
                    RewardID: 0,
                    Name: null,
                    Status: true,
                    _originalStatus: true,
                    PointsThreshold: null,
                    RewardType: 'fixed_amount_off',
                    Value: null,
                    AppliesTo: 'AllMenus',
                }
                this.rewardModalVisible = true
            },
            showEditReward (item) {
                this.rewardModalMode = 'edit'
                const isActive = item.Status === 'active'
                this.rewardModel = {
                    RewardID: item.RewardID,
                    Name: item.Name,
                    Status: isActive,
                    _originalStatus: isActive,
                    PointsThreshold: item.PointsThreshold,
                    RewardType: item.RewardType,
                    Value: item.Value,
                    AppliesTo: Array.isArray(item.Scope) && item.Scope.length > 0 ? 'SelectedItems' : 'AllMenus',
                }
                this.rewardModalVisible = true
            },
            async saveReward () {
                const valid = await this.$validator.validateAll()
                if (!valid) return

                const object = {
                    RestaurantID: Number(this.$store.state.selectedRestaurantId),
                    RewardID: this.rewardModel.RewardID || 0,
                    Name: this.rewardModel.Name,
                    PointsThreshold: Number(this.rewardModel.PointsThreshold),
                    RewardType: this.rewardModel.RewardType,
                    Value: this.rewardModel.RewardType === 'free_item' ? null : Number(this.rewardModel.Value),
                    Scope: this.rewardModel.AppliesTo === 'AllMenus' ? [] : null,
                }

                this.loading = true
                try {
                    const res = await this.$api.loyaltyAdmin.SaveReward(object)
                    if (!res.success) {
                        this.$showError(res.message)
                        return
                    }

                    const rewardID = object.RewardID || res.RewardID
                    const statusChanged = this.rewardModalMode === 'edit' && this.rewardModel.Status !== this.rewardModel._originalStatus
                    if (statusChanged && rewardID) {
                        const statusRes = await this.$api.loyaltyAdmin.SetRewardStatus({
                            RestaurantID: Number(this.$store.state.selectedRestaurantId),
                            RewardID: rewardID,
                            Status: this.rewardModel.Status ? 'active' : 'inactive',
                        })
                        if (!statusRes.success) {
                            this.$showError(statusRes.message)
                        }
                    }

                    this.rewardModalVisible = false
                    this.GetSettings()
                } finally {
                    this.loading = false
                }
            },
            showEditOrderType (item) {
                this.orderTypeModel = {...item}
                this.orderTypeModalVisible = true
            },
            async saveOrderTypeFactors () {
                const valid = await this.$validator.validateAll()
                if (!valid) return

                const factors = this.orderTypeFactors.map(item => {
                    if (item.OrderTypeCode === this.orderTypeModel.OrderTypeCode) {
                        return {
                            OrderTypeCode: item.OrderTypeCode,
                            Multiplier: Math.round(Number(this.orderTypeModel.Multiplier)),
                            FixedBonusPoints: Math.round(Number(this.orderTypeModel.FixedBonusPoints)),
                        }
                    }
                    return {
                        OrderTypeCode: item.OrderTypeCode,
                        Multiplier: Math.round(Number(item.Multiplier)),
                        FixedBonusPoints: Math.round(Number(item.FixedBonusPoints)),
                    }
                })

                const object = {
                    RestaurantID: Number(this.$store.state.selectedRestaurantId),
                    Factors: factors,
                }

                this.loading = true
                try {
                    const res = await this.$api.loyaltyAdmin.SaveOrderTypeFactors(object)
                    if (res.success) {
                        this.orderTypeModalVisible = false
                        this.GetSettings()
                    } else {
                        this.$showError(res.message)
                    }
                } finally {
                    this.loading = false
                }
            },
        }
    }
</script>

<style scoped lang="scss">
    .form-control:disabled, .form-control[readonly]{
        background: #fff !important;
    }
    .removeBorder{
        border: none !important;
    }
    .deactivePrepend{
        .input-group-prepend{
            background: #fff !important;
            border: none !important;
        }
        .input-group-text{
            background: #fff !important;
            border: none !important;
        }
    }
</style>
